import * as THREE from 'three'
import type { ImportedModelTopologyHostTransformProjectionPort } from '../../../types/imported-model/ImportedModelTopologyHostTransformProjection'
import { FeatureResultMesh } from '../../mesh/feature'
import { addObject3DToParent } from '../../utils/scene-object/addObject3DToParent'
import type {
  ImportedPartBodyBrepBinding,
  ImportedPartBodyTopologyAsset,
} from '../loaders/types'
import { ImportedModelObjectMetadataService, type ImportedModelObjectMetadata } from '../metadata'
import {
  areImportedBodyBindingIdsEquivalent,
  normalizeImportedBodyBindingId,
  resolveImportedBodyDefinitionBindingId,
  resolveStableImportedBodyBindingId,
} from '../utils'
import { ImportedTopologyCompositeFaceGeometryProvider } from './ImportedTopologyCompositeFaceGeometryProvider'
import { ImportedMeshTopologyBinding } from './ImportedMeshTopologyBinding'
import { ImportedTopologyOwnerIndex } from './ImportedTopologyOwnerIndex'
import { resolveImportedPickHostObjectId } from '../geometry'
import { areAssemblyNodeIdentityEquivalent } from '../../assembly/utils/AssemblyNodePath'

/**
 * 导入拓扑宿主标记。
 */
const IMPORTED_TOPOLOGY_HOST_FLAG = 'isImportedModelTopologyHost'

/**
 * scoped 项目子树切换中的 body occurrence 重绑定描述。
 *
 * 设计说明：source 与 target 都是完整 bodyBindingId，调用方必须从切换前后 hierarchy
 * 的同一 body 条目生成映射。repository 不根据 Object3D 父链猜测零件 owner。
 */
export interface ImportedModelBodyTopologyOccurrenceRebinding {
  /**
   * 来源 hierarchy 对应的真实场景对象 ID。
   *
   * 完整 GLB 可以让多个 occurrence 共享同一个定义级 bodyBindingId，因此 repository 必须先按
   * 可见对象作用域定位来源记录，再应用 body 身份映射。历史调用未提供时继续使用单作用域兼容语义。
   */
  readonly sourceObjectId?: string
  /** 包解析阶段注册到 repository 的 bodyBindingId。 */
  readonly sourceBodyBindingId: string
  /** 最终项目零件 occurrence 对应的 bodyBindingId。 */
  readonly targetBodyBindingId: string
  /** 最终项目零件节点 ID；由 metadata 同步链路消费并用于诊断。 */
  readonly targetAssemblyNodeId: string
}

/**
 * 判断 body occurrence 映射是否属于当前 Object3D 的显式来源作用域。
 *
 * 设计说明：完整 GLB/多个归档包可以复用定义级 bodyBindingId，来源对象是区分独立 occurrence
 * 的必要维度。visible mesh 既可能位于组件祖先链下，也可能通过 selection proxy 指向语义节点。
 *
 * @param object 当前 topology Body 对应的运行时对象。
 * @param rebinding 候选 body occurrence 映射。
 * @returns 映射未限定对象或命中当前对象/祖先的显式选择关系时返回 `true`。
 */
export function isImportedModelBodyOccurrenceRebindingApplicableToObject(
  object: THREE.Object3D,
  rebinding: ImportedModelBodyTopologyOccurrenceRebinding,
): boolean {
  const sourceObjectId = rebinding.sourceObjectId?.trim() ?? ''
  if (!sourceObjectId) {
    return true
  }

  let current: THREE.Object3D | null = object
  while (current) {
    if (current.uuid === sourceObjectId) {
      return true
    }
    const metadata = ImportedModelObjectMetadataService.readOwnMetadata(current)
    if (
      metadata?.selectionProxyObjectId?.trim() === sourceObjectId
      || metadata?.hierarchy?.meta?.selectionObjectId?.trim() === sourceObjectId
    ) {
      return true
    }
    current = current.parent
  }
  return false
}

/**
 * visible mesh 上挂载的 primitive 面范围。
 */
interface ImportedMeshPrimitiveFaceRange {
  primitiveId: string
  startFaceIndex: number
  endFaceIndex: number
}

/**
 * 导入拓扑仓库根节点索引。
 *
 * 设计说明：
 * 1. 选择链拿到的往往是场景对象本身，因此仓库必须支持“由任意命中对象反查所属仓库”；
 * 2. 这里用 `WeakMap` 维护根节点到仓库的映射，不把仓库状态散落到 Vue 或全局单例；
 * 3. `WeakMap` 天然跟随对象释放，避免 imported 模型卸载后形成悬挂引用。
 */
const importedTopologyRepositoryByRoot = new WeakMap<THREE.Object3D, ImportedModelTopologyRepository>()

/**
 * 已提升到仓库根之外的运行时拓扑宿主到仓库的精确反查索引。
 *
 * `FeatureResultMesh` 可能为了保持独立显示而从直接 Mesh occurrence 下提升为其兄弟节点。
 * 该索引只补充选择反查，不代表仓库生命周期所有权；删除和迁移仍必须通过根索引判断。
 */
const importedTopologyRepositoryByLookupAnchor = new WeakMap<
  THREE.Object3D,
  ImportedModelTopologyRepository
>()

/**
 * 注册一个脱离仓库根父链的运行时反查锚点。
 *
 * @param anchor 需要支持仓库反查的拓扑宿主。
 * @param repository 真实拥有该宿主的仓库。
 * @throws 锚点仍由另一仓库持有时抛出错误，禁止静默覆盖生命周期所有权。
 */
function registerImportedModelTopologyRepositoryLookupAnchor(
  anchor: THREE.Object3D,
  repository: ImportedModelTopologyRepository,
): void {
  const existingRepository = importedTopologyRepositoryByLookupAnchor.get(anchor)
  if (existingRepository && existingRepository !== repository) {
    throw new Error(`导入拓扑宿主反查锚点存在仓库冲突：${anchor.uuid}`)
  }
  importedTopologyRepositoryByLookupAnchor.set(anchor, repository)
}

/**
 * 仅在锚点仍归属指定仓库时解除运行时反查。
 *
 * 转移流程可能先把同一宿主注册给目标仓库；带所有者校验可以避免来源仓库误删新绑定。
 *
 * @param anchor 待解除的拓扑宿主。
 * @param repository 当前释放锚点的仓库。
 */
function unregisterImportedModelTopologyRepositoryLookupAnchor(
  anchor: THREE.Object3D,
  repository: ImportedModelTopologyRepository,
): void {
  if (importedTopologyRepositoryByLookupAnchor.get(anchor) === repository) {
    importedTopologyRepositoryByLookupAnchor.delete(anchor)
  }
}

/**
 * 注册 imported 拓扑仓库。
 *
 * @param root 仓库作用域根节点。
 * @param repository 仓库实例。
 */
export function registerImportedModelTopologyRepository(
  root: THREE.Object3D,
  repository: ImportedModelTopologyRepository,
): void {
  importedTopologyRepositoryByRoot.set(root, repository)
}

/**
 * 反查场景对象所属的 imported 拓扑仓库。
 *
 * @param object 任意场景对象。
 * @returns 命中的仓库；不存在时返回 `null`。
 */
export function resolveImportedModelTopologyRepository(
  object: THREE.Object3D | null | undefined,
): ImportedModelTopologyRepository | null {
  let current = object ?? null
  while (current) {
    const repository = importedTopologyRepositoryByRoot.get(current)
      ?? importedTopologyRepositoryByLookupAnchor.get(current)
    if (repository) {
      return repository
    }
    current = current.parent
  }
  return null
}

/**
 * 读取指定根节点自身直接注册的 imported 拓扑仓库。
 *
 * 设计说明：删除、所有权迁移等生命周期操作必须区分“当前对象自己拥有仓库”与
 * “当前对象仅通过 parent 链继承祖先仓库”。继续使用向上查找会在删除子树时误释放兄弟组件。
 *
 * @param root 待查询的仓库作用域根。
 * @returns 直接注册在该根上的仓库；不存在时返回 `null`。
 */
export function resolveImportedModelTopologyRepositoryAtRoot(
  root: THREE.Object3D | null | undefined,
): ImportedModelTopologyRepository | null {
  return root ? importedTopologyRepositoryByRoot.get(root) ?? null : null
}

/**
 * 收集指定场景作用域中的导入拓扑仓库。
 *
 * 该查询属于仓库索引本身，而不是拓扑激活流程。放在仓库模块中可以让模型体同步只依赖
 * 稳定的仓库查询接口，避免模型体同步与拓扑激活模块形成循环依赖。
 *
 * @param root 当前查询作用域根节点。
 * @returns 去重后的导入拓扑仓库集合。
 */
export function collectImportedModelTopologyRepositories(
  root: THREE.Object3D,
): Set<ImportedModelTopologyRepository> {
  return new Set(collectImportedModelTopologyRepositorySceneAnchors(root).keys())
}

/**
 * 收集场景子树中的 imported topology 仓库及其物理场景锚点。
 *
 * 实例化后 visible source Mesh 可以脱离物理树，但仓库根、topology host 或其它运行时锚点
 * 仍位于场景中。模型体注册必须使用实际被遍历到的锚点证明场景归属，不能要求 display Mesh
 * 自身继续保留 parent。
 *
 * @param root 当前场景或待扫描子树。
 * @returns 仓库到首个物理场景锚点的稳定映射。
 */
export function collectImportedModelTopologyRepositorySceneAnchors(
  root: THREE.Object3D,
): Map<ImportedModelTopologyRepository, THREE.Object3D> {
  const anchorsByRepository = new Map<ImportedModelTopologyRepository, THREE.Object3D>()
  const rootRepository = resolveImportedModelTopologyRepository(root)
  if (rootRepository) {
    anchorsByRepository.set(rootRepository, root)
  }

  root.traverse((object) => {
    const repository = resolveImportedModelTopologyRepository(object)
    if (repository && !anchorsByRepository.has(repository)) {
      anchorsByRepository.set(repository, object)
    }
  })

  return anchorsByRepository
}

/**
 * 注销 imported 拓扑仓库。
 *
 * @param root 仓库作用域根节点。
 */
export function unregisterImportedModelTopologyRepository(root: THREE.Object3D): void {
  importedTopologyRepositoryByRoot.delete(root)
}

/**
 * 创建导入拓扑 pick host 几何体。
 *
 * @param meshData topo sidecar 提供的交互拓扑网格。
 * @returns pick host 几何体。
 */
function createImportedTopologyGeometry(meshData: MeshData): THREE.BufferGeometry {
  const geometry = new THREE.BufferGeometry()
  geometry.setAttribute('position', new THREE.Float32BufferAttribute(meshData.positions, 3))
  geometry.setAttribute('normal', new THREE.Float32BufferAttribute(meshData.normals, 3))
  if (meshData.indices) {
    geometry.setIndex(new THREE.BufferAttribute(meshData.indices, 1))
  }
  geometry.computeBoundingBox()
  geometry.computeBoundingSphere()
  return geometry
}

/**
 * 创建不可见的 pick host 材质。
 *
 * 设计说明：
 * 1. pick host 只负责第二次局部拓扑仲裁，不承担任何默认外观渲染职责；
 * 2. 因此材质必须完全不可见，但对象本身仍保留在场景图中以承载临时 Feature 代理；
 * 3. `colorWrite=false` 与 `material.visible=false` 配合，可避免这份隐藏宿主进入可见渲染结果。
 *
 * @returns pick host 材质。
 */
function createImportedTopologyHostMaterial(): THREE.MeshBasicMaterial {
  const material = new THREE.MeshBasicMaterial({
    color: 0x000000,
    transparent: true,
    opacity: 0,
    depthWrite: false,
    depthTest: true,
    side: THREE.DoubleSide,
  })
  material.colorWrite = false
  material.visible = false
  return material
}

/**
 * 读取 visible mesh 上的 primitive 面范围。
 *
 * @param mesh visible mesh。
 * @returns primitive 面范围列表。
 */
function resolveVisibleMeshPrimitiveFaceRanges(mesh: THREE.Mesh): ImportedMeshPrimitiveFaceRange[] {
  const localBinding = mesh.userData?.partAssetLocal as {
    primitiveFaceRanges?: ImportedMeshPrimitiveFaceRange[]
  } | undefined

  return Array.isArray(localBinding?.primitiveFaceRanges)
    ? localBinding.primitiveFaceRanges
    : []
}

/**
 * 根据 faceIndex 解析当前命中的 primitiveId。
 *
 * @param mesh 命中的 visible mesh。
 * @param faceIndex 命中的三角面索引。
 * @returns 对应的 primitiveId；无法解析时返回 `null`。
 */
function resolvePrimitiveIdFromVisibleHit(mesh: THREE.Mesh, faceIndex: number): string | null {
  const primitiveFaceRanges = resolveVisibleMeshPrimitiveFaceRanges(mesh)
  const matchedRange = primitiveFaceRanges.find((range) => (
    faceIndex >= range.startFaceIndex && faceIndex <= range.endFaceIndex
  ))
  return matchedRange?.primitiveId ?? null
}

/**
 * 导入 metadata 来源对象快照。
 */
interface ImportedMetadataSource {
  /** 实际提供 metadata 的场景对象。 */
  object: THREE.Object3D
  /** 已经过服务克隆隔离的导入 metadata。 */
  metadata: ImportedModelObjectMetadata
}

/**
 * 从对象自身或祖先链查找第一份导入 metadata，并保留来源对象。
 *
 * @param object 起始 Object3D。
 * @returns metadata 来源对象快照；不存在时返回 null。
 */
function resolveImportedMetadataSourceFromSelfOrAncestors(
  object: THREE.Object3D,
): ImportedMetadataSource | null {
  let current: THREE.Object3D | null = object
  while (current) {
    const metadata = ImportedModelObjectMetadataService.peekMetadata(current)
    if (metadata) {
      return {
        object: current,
        metadata,
      }
    }
    current = current.parent
  }
  return null
}

/**
 * 解析 pick host 应写入的稳定选择代理 ID。
 *
 * @param source metadata 来源对象快照。
 * @returns 可用于回收到导入语义节点的 Object3D uuid。
 */
function resolvePickHostSelectionProxyObjectId(source: ImportedMetadataSource): string | undefined {
  const explicitProxyObjectId = source.metadata.selectionProxyObjectId?.trim()
  if (explicitProxyObjectId) {
    return explicitProxyObjectId
  }

  const hierarchySelectionObjectId = source.metadata.hierarchy?.meta?.selectionObjectId?.trim()
  if (hierarchySelectionObjectId) {
    return hierarchySelectionObjectId
  }

  const semanticKind = source.metadata.hierarchy?.meta?.semanticKind?.trim()
  return semanticKind === 'part' || semanticKind === 'assembly'
    ? source.object.uuid
    : undefined
}

/**
 * 为导入拓扑 pick host 继承 visible mesh 的导入语义 metadata。
 *
 * 设计说明：
 * 1. FeatureFace / FeatureLine / FeaturePoint 是按需挂载的拓扑代理，交互生命周期中 parent 链可能短暂脱离 visible mesh；
 * 2. 因此 pick host 必须自身携带稳定的 importedModel metadata，避免选中面时只能依赖 `host.parent` 回溯语义节点；
 * 3. 优先读取 visible mesh 自身 metadata，缺失时再向祖先兜底，兼容装配实例化阶段只在语义宿主上写入 metadata 的场景。
 *
 * @param host 当前 body 的拓扑 pick host。
 * @param bodyRecord body 拓扑运行时记录。
 */
function inheritImportedMetadataToPickHost(
  host: FeatureResultMesh,
  bodyRecord: ImportedMeshTopologyBinding,
): void {
  const source = resolveImportedMetadataSourceFromSelfOrAncestors(bodyRecord.visibleMesh)
  if (!source) {
    return
  }

  ImportedModelObjectMetadataService.writeMetadata(host, {
    ...source.metadata,
    selectionProxyObjectId: resolvePickHostSelectionProxyObjectId(source),
  })
}

/**
 * 判断对象是否为 imported topology pick host。
 *
 * @param object 目标对象。
 * @returns 是否为 imported pick host。
 */
export function isImportedModelTopologyHost(
  object: THREE.Object3D | null | undefined,
): object is FeatureResultMesh {
  return Boolean(
    object instanceof FeatureResultMesh
    && object.userData?.[IMPORTED_TOPOLOGY_HOST_FLAG] === true,
  )
}

/**
 * 导入 body 级拓扑仓库。
 *
 * 设计说明：
 * 1. 仓库只管理 imported body 的运行时拓扑资源，不修改可见 GLB mesh 的材质与结构语义；
 * 2. 仓库只保留按需创建 pick host 的入口，FeatureLine 不在加载阶段批量挂载；
 * 3. 所有可释放资源都在仓库中集中追踪，形成“创建处 -> 销毁处”的完整生命周期闭包。
 */
export class ImportedModelTopologyRepository {
  /** 全部 Repository 的 Body 成员变化共享的单调语义修订。 */
  private static semanticRevision = 0

  /**
   * 读取 imported 第一阶段可见 Mesh 身份依赖的拓扑修订。
   *
   * @returns 任一仓库注册、迁移或释放 Body 记录后递增的单调值。
   */
  public static getSemanticRevision(): number {
    return ImportedModelTopologyRepository.semanticRevision
  }

  private readonly bodyRecordsByBindingId = new Map<string, ImportedMeshTopologyBinding>()

  /** 注册/重绑定时维护的 Body occurrence 归属索引，不在 Hover 扫描全仓库。 */
  private readonly topologyOwnerIndex = new ImportedTopologyOwnerIndex()

  /** pick host 当前采用的只读成员版本，用于热态复用及多材质成员变化失效。 */
  private readonly ownerRecordsByPickHost = new Map<FeatureResultMesh, readonly ImportedMeshTopologyBinding[]>()

  private readonly bodyRecordsByVisibleMeshId = new Map<string, ImportedMeshTopologyBinding[]>()

  private readonly bodyBindingIdByVisibleMeshUuidAndPrimitiveId = new Map<string, Map<string, string>>()

  /** 已脱离仓库根父链、但仍需参与选择反查的业务 source Mesh 锚点。 */
  private readonly runtimeLookupAnchors = new Set<THREE.Object3D>()

  /** Viewer 运行时注入的 topology host 变换投影端口。 */
  private topologyHostTransformProjectionPort: ImportedModelTopologyHostTransformProjectionPort | null = null

  /** 已成功绑定变换投影的 pick host 到 source Mesh 索引。 */
  private readonly projectedSourceMeshByPickHost = new Map<FeatureResultMesh, THREE.Mesh>()

  /**
   * 注册一个脱离仓库根父链的业务对象反查锚点。
   *
   * 实例化层会把 source Mesh 从主场景树移除，但拓扑精选仍必须用同一业务对象定位本仓库。
   * 锚点只承担运行时查询，不改变仓库所有权、Body 记录或持久化身份。
   *
   * @param anchor 需要继续解析到本仓库的业务对象。
   */
  public registerRuntimeLookupAnchor(anchor: THREE.Object3D): void {
    registerImportedModelTopologyRepositoryLookupAnchor(anchor, this)
    this.runtimeLookupAnchors.add(anchor)
  }

  /**
   * 解除一个运行时业务对象反查锚点。
   *
   * @param anchor 即将恢复父链、迁组或销毁的业务对象。
   */
  public unregisterRuntimeLookupAnchor(anchor: THREE.Object3D): void {
    unregisterImportedModelTopologyRepositoryLookupAnchor(anchor, this)
    this.runtimeLookupAnchors.delete(anchor)
  }

  /**
   * @param root 仓库作用域根节点。
   */
  public constructor(private readonly root: THREE.Object3D) {}

  /**
   * 读取仓库作用域根节点。
   *
   * 设计说明：
   * imported topology 的 pick host、FeatureFace/FeatureLine/FeaturePoint 只是交互代理；
   * Mate 预览、对齐切换和拖动重求解必须把 transform 写回仓库作用域根，才能保证可见 mesh
   * 与拓扑代理使用同一套组件位姿。
   *
   * @returns 当前 imported topology 仓库对应的组件作用域根。
   */
  public getRoot(): THREE.Object3D {
    return this.root
  }

  /**
   * 设置 topology pick host 的派生变换投影端口。
   *
   * 实例 Unit 重建时会再次注入同一个端口对象，因此该入口必须先解除旧绑定再扫描当前已存在
   * host，不能仅按对象引用跳过。Repository 不保存 Unit，只保存成功绑定的 source/host 对。
   *
   * @param port 当前 Viewer 的投影端口；传入 null 时解除全部运行时绑定。
   */
  public setTopologyHostTransformProjectionPort(
    port: ImportedModelTopologyHostTransformProjectionPort | null,
  ): void {
    const previousPort = this.topologyHostTransformProjectionPort
    if (previousPort) {
      this.projectedSourceMeshByPickHost.forEach((sourceMesh, pickHost) => {
        previousPort.detach(sourceMesh, pickHost)
      })
    }
    this.projectedSourceMeshByPickHost.clear()
    this.topologyHostTransformProjectionPort = port
    if (!port) {
      return
    }

    /** 同一个多材质 Body 的多条记录可能共享 host，按对象去重后只建立一条变换绑定。 */
    const visitedPickHosts = new Set<FeatureResultMesh>()
    this.bodyRecordsByBindingId.forEach((bodyRecord) => {
      const pickHost = bodyRecord.getPickHost()
      /** 空闲缓存宿主已经脱离 Scene；再次命中时由 ensurePickHost 以当前 Unit 端口重新绑定。 */
      if (!pickHost || !pickHost.parent || visitedPickHosts.has(pickHost)) {
        return
      }
      const ownerRecords = this.ownerRecordsByPickHost.get(pickHost) ?? [bodyRecord]
      const targetParent = this.resolveTopologyHostParent(ownerRecords)
      if (this.syncPickHostTransformProjection(bodyRecord.visibleMesh, pickHost, targetParent)) {
        visitedPickHosts.add(pickHost)
      }
    })
  }

  /**
   * 注册当前作用域下的一个 visible mesh。
   *
   * @param mesh visible mesh。
   * @param bodyBindings 该 mesh 对应的 body 绑定集合。
   * @param topologyAssetsById body 级拓扑资产表。
   * @throws 当前 Mesh 或 Body binding 已注册，或任一绑定不满足身份/范围契约时抛出错误。
   */
  public registerVisibleMesh(
    mesh: THREE.Mesh,
    bodyBindings: ImportedPartBodyBrepBinding[],
    topologyAssetsById: Record<string, ImportedPartBodyTopologyAsset>,
  ): void {
    if (this.bodyRecordsByVisibleMeshId.has(mesh.uuid)) {
      throw new Error(`导入拓扑重复注册 visible Mesh: ${mesh.uuid}`)
    }

    const bodyRecords: ImportedMeshTopologyBinding[] = []
    const bodyBindingIdByPrimitiveId = new Map<string, string>()
    const pendingBodyBindingIds = new Set<string>()
    const displaySource = ImportedModelObjectMetadataService.resolveDisplaySource(mesh)

    bodyBindings.forEach((bodyBinding) => {
      const topologyAsset = topologyAssetsById[bodyBinding.bodyBindingId]
      if (!topologyAsset) {
        return
      }
      if (pendingBodyBindingIds.has(bodyBinding.bodyBindingId)) {
        throw new Error(`导入拓扑重复注册 Body binding: ${bodyBinding.bodyBindingId}`)
      }

      /**
       * GLTFLoader 为多 primitive mesh 创建独立子 Mesh；complete-GLB association 是该子 Mesh
       * 的权威 primitive 身份。历史合并 geometry 没有 association，继续使用 Body 绑定的显式集合。
       */
      const primitiveIndices = displaySource
        ? [displaySource.primitiveIndex]
        : bodyBinding.primitiveIndices
      if (
        primitiveIndices.length === 0
        || primitiveIndices.some((primitiveIndex) => (
          !Number.isInteger(primitiveIndex) || primitiveIndex < 0
        ))
      ) {
        /** 缺少明确 primitive 集合时禁止根据名称或兄弟顺序猜测，避免把命中路由到错误 Body。 */
        throw new Error(
          `导入 Mesh 缺少明确 primitive 身份: mesh=${mesh.uuid}, body=${bodyBinding.bodyBindingId}`,
        )
      }
      /** 正式绑定对象统一持有 Mesh 局部范围、Body 拓扑与 pick host 生命周期。 */
      const record = new ImportedMeshTopologyBinding({
        mesh,
        primitiveIndices,
        bodyBinding,
        topologyAsset,
      })

      pendingBodyBindingIds.add(bodyBinding.bodyBindingId)
      bodyBinding.primitiveIds.forEach((primitiveId) => {
        bodyBindingIdByPrimitiveId.set(primitiveId, bodyBinding.bodyBindingId)
      })
      bodyRecords.push(record)
    })

    if (bodyRecords.length > 0) {
      /** 全部绑定完成构造与冲突校验后再统一提交，异常路径不会留下半注册索引。 */
      bodyRecords.forEach((record) => {
        this.indexBodyRecord(record)
      })
      /**
       * visible mesh 的材质是 GLB 显示语义的一部分，拓扑仓库只负责建立可拾取
       * body 记录与 pick host，不能在 topo sidecar 挂载时覆盖后端导出的实体颜色。
       */
      this.bodyRecordsByVisibleMeshId.set(mesh.uuid, bodyRecords)
      this.bodyBindingIdByVisibleMeshUuidAndPrimitiveId.set(mesh.uuid, bodyBindingIdByPrimitiveId)
    }
  }

  /**
   * 接收另一仓库转移过来的 visible mesh 记录。
   *
   * 设计说明：记录对象中可能已经持有按需创建的 pick host，因此不能通过
   * `registerVisibleMesh()` 重建记录。这里在写入任何索引前完成全部冲突校验，
   * 保证失败时来源仓库和目标仓库都保持原状。
   *
   * @param mesh 记录所属 visible mesh。
   * @param records 待接收的原始 body 记录。
   */
  private adoptVisibleMeshRecords(
    mesh: THREE.Mesh,
    records: readonly ImportedMeshTopologyBinding[],
  ): void {
    this.assertCanAdoptVisibleMeshRecords(mesh, records)

    const bodyBindingIdByPrimitiveId = new Map<string, string>()
    records.forEach((record) => {
      this.indexBodyRecord(record)
      record.bodyBinding.primitiveIds.forEach((primitiveId) => {
        bodyBindingIdByPrimitiveId.set(primitiveId, record.bodyBindingId)
      })
    })
    this.bodyRecordsByVisibleMeshId.set(mesh.uuid, [...records])
    this.bodyBindingIdByVisibleMeshUuidAndPrimitiveId.set(mesh.uuid, bodyBindingIdByPrimitiveId)
  }

  /**
   * 校验目标仓库是否可以完整接收一组 visible mesh 记录。
   *
   * @param mesh 记录所属 visible mesh。
   * @param records 待接收的 body 记录。
   */
  private assertCanAdoptVisibleMeshRecords(
    mesh: THREE.Mesh,
    records: readonly ImportedMeshTopologyBinding[],
  ): void {
    const existingMeshRecords = this.bodyRecordsByVisibleMeshId.get(mesh.uuid)
    if (existingMeshRecords && existingMeshRecords.length > 0) {
      throw new Error(
        `导入拓扑记录迁移冲突：目标仓库已持有 visible mesh，targetRoot=${this.root.uuid}, mesh=${mesh.uuid}`,
      )
    }

    records.forEach((record) => {
      if (record.visibleMesh !== mesh) {
        throw new Error(
          `导入拓扑记录迁移失败：body 记录与 visible mesh 不一致，targetRoot=${this.root.uuid}, bodyBindingId=${record.bodyBindingId}`,
        )
      }
      const existingRecord = this.bodyRecordsByBindingId.get(record.bodyBindingId)
      if (existingRecord && existingRecord !== record) {
        throw new Error(
          `导入拓扑记录迁移冲突：目标仓库存在重复 bodyBindingId，targetRoot=${this.root.uuid}, bodyBindingId=${record.bodyBindingId}`,
        )
      }
    })
  }

  /**
   * 在不修改任何索引的前提下预检 visible mesh 记录迁移。
   *
   * 多 mesh 子树迁移会先对全部候选执行预检，再开始实际写入，避免后一个 mesh 冲突时
   * 前面的记录已经离开来源仓库。
   *
   * @param mesh 需要转移所有权的 visible mesh。
   * @param targetRepository 目标仓库。
   */
  public assertCanTransferVisibleMeshRecordsTo(
    mesh: THREE.Mesh,
    targetRepository: ImportedModelTopologyRepository,
  ): void {
    if (targetRepository === this) {
      return
    }
    const records = this.bodyRecordsByVisibleMeshId.get(mesh.uuid) ?? []
    if (records.length > 0) {
      targetRepository.assertCanAdoptVisibleMeshRecords(mesh, records)
    }
  }

  /**
   * 将指定 visible mesh 的全部 body 记录无损转移到另一个仓库。
   *
   * 设计说明：Object3D 子树换父节点会改变仓库的祖先查找边界。转移操作只变更三个仓库索引，
   * 不销毁 topology asset、pick host、几何体或材质，从而让资源生命周期跟随新的 occurrence 根。
   *
   * @param mesh 需要转移所有权的 visible mesh。
   * @param targetRepository 新 occurrence 根对应的目标仓库。
   * @returns 实际转移的 body 记录数量。
   */
  public transferVisibleMeshRecordsTo(
    mesh: THREE.Mesh,
    targetRepository: ImportedModelTopologyRepository,
  ): number {
    if (targetRepository === this) {
      return 0
    }

    const records = this.bodyRecordsByVisibleMeshId.get(mesh.uuid) ?? []
    if (records.length === 0) {
      return 0
    }

    this.assertCanTransferVisibleMeshRecordsTo(mesh, targetRepository)
    const transferredRecordSet = new Set(records)
    const sharedHostCrossesRepositoryBoundary = records.some((record) => {
      const pickHost = record.getPickHost()
      return Boolean(
        pickHost
        && [...this.bodyRecordsByBindingId.values()].some((candidate) => (
          candidate.getPickHost() === pickHost && !transferredRecordSet.has(candidate)
        )),
      )
    })
    if (sharedHostCrossesRepositoryBoundary) {
      /** 一个拓扑宿主不能同时归属两个 Repository，跨边界时释放并由双方按需重建。 */
      records.forEach((record) => {
        this.disposePickHostForRecord(record)
      })
    }
    const transferredPickHosts = new Set(
      records
        .map((record) => record.getPickHost())
        .filter((host): host is FeatureResultMesh => Boolean(host)),
    )
    transferredPickHosts.forEach((pickHost) => {
      this.releasePickHostTransformProjection(pickHost)
      unregisterImportedModelTopologyRepositoryLookupAnchor(pickHost, this)
    })
    targetRepository.adoptVisibleMeshRecords(mesh, records)
    transferredPickHosts.forEach((pickHost) => {
      registerImportedModelTopologyRepositoryLookupAnchor(pickHost, targetRepository)
      const ownerRecords = this.ownerRecordsByPickHost.get(pickHost)
      if (ownerRecords) {
        targetRepository.ownerRecordsByPickHost.set(pickHost, ownerRecords)
        this.ownerRecordsByPickHost.delete(pickHost)
      }
      const targetOwnerRecords = targetRepository.ownerRecordsByPickHost.get(pickHost) ?? records
      targetRepository.syncPickHostTransformProjection(
        mesh,
        pickHost,
        targetRepository.resolveTopologyHostParent(targetOwnerRecords),
      )
    })
    records.forEach((record) => {
      if (this.bodyRecordsByBindingId.get(record.bodyBindingId) === record) {
        this.bodyRecordsByBindingId.delete(record.bodyBindingId)
        this.topologyOwnerIndex.remove(record)
      }
    })
    this.bodyRecordsByVisibleMeshId.delete(mesh.uuid)
    this.bodyBindingIdByVisibleMeshUuidAndPrimitiveId.delete(mesh.uuid)
    ImportedModelTopologyRepository.semanticRevision += 1
    return records.length
  }

  /**
   * 判断仓库中是否已注册任何 body 记录。
   *
   * @returns 是否存在可交互 body。
   */
  public hasRecords(): boolean {
    return this.bodyRecordsByBindingId.size > 0
  }

  /**
   * 读取当前仓库管理的 body binding ID 快照。
   *
   * @returns body binding ID 列表。
   */
  public listBodyBindingIds(): string[] {
    return [...this.bodyRecordsByBindingId.keys()]
  }

  /**
   * 读取仓库当前持有的 body 记录快照。
   *
   * @returns 保持仓库注册顺序的 body 记录；调用方不得修改记录内容。
   */
  public listBodyRecords(): readonly ImportedMeshTopologyBinding[] {
    return [...this.bodyRecordsByBindingId.values()]
  }

  /**
   * 按切换前后 hierarchy 生成的明确映射重绑定 body occurrence。
   *
   * 设计说明：
   * 1. 项目子树切换会先创建包内 topology repository，再把场景层级折叠到项目 occurrence；
   * 2. hierarchy、selection metadata 与 repository 必须在提交前使用同一 bodyBindingId，否则 Mate
   *    恢复会重新读回包内 owner，pick host 也无法跟随最终零件变换；
   * 3. 映射只服务 scoped 切换，不读取父链 metadata，也不改变普通加载和直接装配的身份规则。
   *
   * @param rebindings hierarchy 对齐后生成的一对一 occurrence 映射。
   * @param visibleMeshes 可选的精确 visible mesh 范围；所有权转移事务使用它隔离本次迁移记录。
   * @returns 实际发生 occurrence 变化的 body 记录数量。
   */
  public rebindBodyOccurrences(
    rebindings: readonly ImportedModelBodyTopologyOccurrenceRebinding[],
    visibleMeshes?: ReadonlySet<THREE.Mesh>,
  ): number {
    const records = [...this.bodyRecordsByBindingId.values()]
    const nextBodyBindingIdByRecord = new Map<ImportedMeshTopologyBinding, string>()

    records.forEach((record) => {
      /** 同一目标仓库可以持有多个包挂载；显式范围外的记录必须保持原身份。 */
      if (visibleMeshes && !visibleMeshes.has(record.visibleMesh)) {
        nextBodyBindingIdByRecord.set(record, record.bodyBindingId)
        return
      }
      const applicableRebindings = rebindings.filter((rebinding) => (
        isImportedModelBodyOccurrenceRebindingApplicableToObject(record.visibleMesh, rebinding)
      ))
      const exactRebinding = applicableRebindings.find((rebinding) => (
        normalizeImportedBodyBindingId(rebinding.sourceBodyBindingId)
        === normalizeImportedBodyBindingId(record.bodyBindingId)
      ))
      const compatibleRebindings = exactRebinding
        ? [exactRebinding]
        : applicableRebindings.filter((rebinding) => (
            areImportedBodyBindingIdsEquivalent(
              rebinding.sourceBodyBindingId,
              record.bodyBindingId,
            )
          ))
      const targetBodyBindingIds = new Set(
        compatibleRebindings
          .map((rebinding) => normalizeImportedBodyBindingId(rebinding.targetBodyBindingId))
          .filter((bodyBindingId) => bodyBindingId.length > 0),
      )
      if (targetBodyBindingIds.size > 1) {
        throw new Error(`导入拓扑 body occurrence 映射存在歧义：${record.bodyBindingId}`)
      }
      nextBodyBindingIdByRecord.set(
        record,
        [...targetBodyBindingIds][0] ?? record.bodyBindingId,
      )
    })

    /** 重建索引前先验证目标 ID 唯一，避免两个 body 被静默覆盖。 */
    const recordByNextBodyBindingId = new Map<string, ImportedMeshTopologyBinding>()
    nextBodyBindingIdByRecord.forEach((nextBodyBindingId, record) => {
      const existingRecord = recordByNextBodyBindingId.get(nextBodyBindingId)
      if (existingRecord && existingRecord !== record) {
        throw new Error(`导入拓扑 body occurrence 重绑定冲突：${nextBodyBindingId}`)
      }
      recordByNextBodyBindingId.set(nextBodyBindingId, record)
    })

    const changedRecords = records.filter((record) => (
      nextBodyBindingIdByRecord.get(record) !== record.bodyBindingId
    ))
    if (changedRecords.length === 0) {
      return 0
    }

    /** 旧 pick host 携带旧 owner，必须在改写索引前释放。 */
    changedRecords.forEach((record) => {
      this.disposePickHost(record.bodyBindingId)
    })

    this.bodyRecordsByBindingId.clear()
    this.topologyOwnerIndex.clear()
    records.forEach((record) => {
      const nextBodyBindingId = nextBodyBindingIdByRecord.get(record) ?? record.bodyBindingId
      if (nextBodyBindingId !== record.bodyBindingId) {
        record.rebindBodyBindingId(nextBodyBindingId)
      }
      this.indexBodyRecord(record)
    })

    this.rebuildVisibleMeshIndexes()

    return changedRecords.length
  }

  /**
   * 精确释放指定 body 记录及其 pick host。
   *
   * 设计说明：根 `.puppy` 的 repository 可能同时持有目标子树与兄弟组件；scoped 切换只能删除
   * 清理计划冻结的目标 ID，不能对外层 repository 执行整仓 dispose。
   *
   * @param bodyBindingIds 需要释放的当前 repository bodyBindingId。
   * @returns 实际释放的记录数量。
   */
  public disposeBodyRecords(bodyBindingIds: readonly string[]): number {
    const targetBodyBindingIds = new Set(
      bodyBindingIds
        .map((bodyBindingId) => normalizeImportedBodyBindingId(bodyBindingId))
        .filter((bodyBindingId) => bodyBindingId.length > 0),
    )
    const records = [...this.bodyRecordsByBindingId.values()]
      .filter((record) => targetBodyBindingIds.has(
        normalizeImportedBodyBindingId(record.bodyBindingId),
      ))

    records.forEach((record) => {
      this.disposePickHost(record.bodyBindingId)
      this.bodyRecordsByBindingId.delete(record.bodyBindingId)
      this.topologyOwnerIndex.remove(record)
    })
    if (records.length > 0) {
      this.rebuildVisibleMeshIndexes()
      ImportedModelTopologyRepository.semanticRevision += 1
    }
    return records.length
  }

  /**
   * 根据主 body 索引重建 visible mesh 与 primitive 反向索引。
   *
   * 设计说明：occurrence 改写和局部记录释放都会改变 bodyBindingId；两个入口必须共享同一重建逻辑，
   * 避免 primitive 命中继续返回旧 ID。
   */
  private rebuildVisibleMeshIndexes(): void {
    this.bodyRecordsByVisibleMeshId.clear()
    this.bodyBindingIdByVisibleMeshUuidAndPrimitiveId.clear()

    this.bodyRecordsByBindingId.forEach((record) => {
      const visibleMeshRecords = this.bodyRecordsByVisibleMeshId.get(record.visibleMesh.uuid) ?? []
      visibleMeshRecords.push(record)
      this.bodyRecordsByVisibleMeshId.set(record.visibleMesh.uuid, visibleMeshRecords)

      const bodyBindingIdByPrimitiveId = this.bodyBindingIdByVisibleMeshUuidAndPrimitiveId
        .get(record.visibleMesh.uuid) ?? new Map<string, string>()
      record.bodyBinding.primitiveIds.forEach((primitiveId) => {
        bodyBindingIdByPrimitiveId.set(primitiveId, record.bodyBindingId)
      })
      this.bodyBindingIdByVisibleMeshUuidAndPrimitiveId.set(
        record.visibleMesh.uuid,
        bodyBindingIdByPrimitiveId,
      )
    })
  }

  /**
   * 根据 body 绑定 ID 读取拓扑记录。
   *
   * @param bodyBindingId body 绑定 ID。
   * @returns body 拓扑记录；不存在时返回 `null`。
   */
  public resolveBodyRecordByBindingId(bodyBindingId: string): ImportedMeshTopologyBinding | null {
    return this.bodyRecordsByBindingId.get(bodyBindingId) ?? null
  }

  /**
   * 解析 body 记录在 Worker 中应使用的 geometryObjectId。
   *
   * @param bodyRecord body 拓扑记录。
   * @returns Worker geometryObjectId。
   */
  public resolveGeometryObjectIdForBodyRecord(
    bodyRecord: ImportedMeshTopologyBinding,
  ): string {
    return resolveImportedPickHostObjectId(bodyRecord.topologyAsset)
  }

  /**
   * 按 bodyBindingId 解析 Worker geometryObjectId。
   *
   * @param bodyBindingId body 绑定 ID。
   * @returns Worker geometryObjectId；未命中时返回 `null`。
   */
  public resolveGeometryObjectIdByBodyBindingId(bodyBindingId: string): string | null {
    const bodyRecord = this.resolveBodyRecordByBindingRef(bodyBindingId)
    return bodyRecord ? this.resolveGeometryObjectIdForBodyRecord(bodyRecord) : null
  }

  /**
   * 按 runtime objectId 解析 Worker geometryObjectId。
   *
   * @param runtimeObjectId pick host / topo metadata / bodyBindingId 等 runtime ID。
   * @returns Worker geometryObjectId；未命中时返回 `null`。
   */
  public resolveGeometryObjectIdByRuntimeObjectId(runtimeObjectId: string): string | null {
    const bodyRecord = this.resolveBodyRecordByBindingRef(runtimeObjectId)
      ?? this.resolveBodyRecordByRuntimeObjectId(runtimeObjectId)
    return bodyRecord ? this.resolveGeometryObjectIdForBodyRecord(bodyRecord) : null
  }

  /**
   * 按稳定或运行时 bodyBindingId 读取 body 记录。
   *
   * 设计说明：
   * `.puppy` Mate 引用保存的是可序列化稳定引用，导入运行时可能为了共享 mesh 追加 `::meshId`。
   * repository 是唯一知道当前 runtime body 记录全集的边界，因此稳定/运行时 ID 的兼容匹配应在这里收口，
   * 而不是散落到 reference、selection 或 UI。
   *
   * @param bodyBindingId 稳定或运行时 bodyBindingId。
   * @param options 可选的装配实例过滤条件。
   * @returns 命中的 body 记录；歧义或缺失时返回 null。
   */
  public resolveBodyRecordByBindingRef(
    bodyBindingId: string | null | undefined,
    options: {
      /** 期望命中的装配节点 ID，用于区分同一 body 的多个实例。 */
      readonly assemblyNodeId?: string | null
    } = {},
  ): ImportedMeshTopologyBinding | null {
    const normalizedBodyBindingId = normalizeImportedBodyBindingId(bodyBindingId)
    if (!normalizedBodyBindingId) {
      return null
    }

    const expectedAssemblyNodeId = options.assemblyNodeId?.trim() ?? ''
    const exactRecord = this.bodyRecordsByBindingId.get(normalizedBodyBindingId)
    if (
      exactRecord
      && (
        !expectedAssemblyNodeId
        || this.isBodyRecordMatchedByAssemblyNodeId(exactRecord, expectedAssemblyNodeId)
      )
    ) {
      return exactRecord
    }

    const stableBodyBindingId = resolveStableImportedBodyBindingId(normalizedBodyBindingId)
    const candidateRecords = [...this.bodyRecordsByBindingId.values()].filter((record) => (
      areImportedBodyBindingIdsEquivalent(record.bodyBindingId, stableBodyBindingId)
    ))
    if (candidateRecords.length === 0) {
      return expectedAssemblyNodeId
        ? this.resolveBodyRecordByDefinitionAndAssemblyNodeId(normalizedBodyBindingId, expectedAssemblyNodeId)
        : null
    }

    if (expectedAssemblyNodeId) {
      const matchedRecord = candidateRecords.find((record) => (
        this.isBodyRecordMatchedByAssemblyNodeId(record, expectedAssemblyNodeId)
      ))
      if (matchedRecord) {
        return matchedRecord
      }
    }

    return candidateRecords.length === 1 ? candidateRecords[0] ?? null : null
  }

  /**
   * 按 body 定义和当前装配 owner 回收旧 occurrence 的 body 记录。
   *
   * 设计说明：
   * 子树版本切换时，Mate 文档会先被提升到 OpenProject 的稳定 occurrence，但 topology repository
   * 可能仍保留包加载阶段注册的局部 occurrence。这里只在调用方提供明确 owner 时回退到 body 定义匹配，
   * 并再次校验运行时 metadata 的装配节点，避免同一 body 定义在兄弟实例之间串用。
   *
   * @param bodyBindingId 调用方传入的当前 occurrence bodyBindingId。
   * @param expectedAssemblyNodeId 期望命中的装配节点 ID。
   * @returns 唯一命中的 body 记录；无命中或歧义时返回 `null`。
   */
  private resolveBodyRecordByDefinitionAndAssemblyNodeId(
    bodyBindingId: string,
    expectedAssemblyNodeId: string,
  ): ImportedMeshTopologyBinding | null {
    const bodyDefinitionId = resolveImportedBodyDefinitionBindingId(bodyBindingId)
    if (!bodyDefinitionId) {
      return null
    }

    const matchedRecords = [...this.bodyRecordsByBindingId.values()].filter((record) => (
      resolveImportedBodyDefinitionBindingId(record.bodyBindingId) === bodyDefinitionId
      && this.isBodyRecordMatchedByAssemblyNodeId(record, expectedAssemblyNodeId)
    ))

    return matchedRecords.length === 1 ? matchedRecords[0] ?? null : null
  }

  /**
   * 判断 body 记录是否属于指定装配节点。
   *
   * @param record body 拓扑记录。
   * @param expectedAssemblyNodeId 期望的装配节点 ID。
   * @returns 是否命中该装配节点。
   */
  private isBodyRecordMatchedByAssemblyNodeId(
    record: ImportedMeshTopologyBinding,
    expectedAssemblyNodeId: string,
  ): boolean {
    const target = ImportedModelObjectMetadataService
      .resolveBodyBindingSelectionTargetFromSelfOrAncestors(record.visibleMesh, record.bodyBindingId)
    const fallbackAssemblyNodeId = ImportedModelObjectMetadataService
      .resolveAssemblyNodeIdFromSelfOrAncestors(record.visibleMesh)
    /**
     * 子装配离线包的 Mate 引用可能使用 URL 编码节点 ID，而父工作区 metadata 已经恢复为语义 ID。
     * bodyBindingId 命中后仍要按统一身份规则过滤实例，避免同一 body 在不同 occurrence 中被误判为缺失。
     */
    return areAssemblyNodeIdentityEquivalent(target?.assemblyNodeId, expectedAssemblyNodeId)
      || areAssemblyNodeIdentityEquivalent(fallbackAssemblyNodeId, expectedAssemblyNodeId)
  }

  /**
   * 根据导入 topology 运行时对象 ID 读取 body 记录。
   *
   * 设计说明：
   * 1. 转换实体引用的 descriptor 可能记录 `bodyBindingId`，也可能记录 pick host 的 `objectId`；
   * 2. 导入模型的 pick host 是按需创建的，因此不能只依赖已挂载的 `pickHost.objectId`；
   * 3. 这里统一兼容 topo sidecar 的 `metadata.objectId` 与 `meshData.id`，让预览、创建、重建链路都能回收到同一条 body 记录。
   *
   * @param objectId topology 运行时对象 ID。
   * @returns body 拓扑记录；未命中时返回 `null`。
   */
  public resolveBodyRecordByRuntimeObjectId(objectId: string): ImportedMeshTopologyBinding | null {
    const normalizedObjectId = objectId.trim()
    if (!normalizedObjectId) {
      return null
    }

    const directBindingRecord = this.bodyRecordsByBindingId.get(normalizedObjectId)
    if (directBindingRecord) {
      return directBindingRecord
    }

    for (const bodyRecord of this.bodyRecordsByBindingId.values()) {
      const geometryObjectId = bodyRecord.topologyAsset.geometryObjectId?.trim() ?? ''
      const metadataObjectId = bodyRecord.topologyAsset.meshData.metadata?.objectId
      const topologyObjectId = typeof metadataObjectId === 'string' ? metadataObjectId.trim() : ''
      const meshDataId = bodyRecord.topologyAsset.meshData.id?.trim() ?? ''
      const pickHostObjectId = bodyRecord.getPickHost()?.objectId ?? ''
      if (
        normalizedObjectId === geometryObjectId
        || normalizedObjectId === topologyObjectId
        || normalizedObjectId === meshDataId
        || normalizedObjectId === pickHostObjectId
      ) {
        return bodyRecord
      }
    }

    return null
  }

  /**
   * 读取某个 visible mesh 对应的全部 body 记录。
   *
   * @param mesh visible mesh。
   * @returns body 记录快照。
   */
  public resolveBodyRecordsByVisibleMesh(mesh: THREE.Mesh): ImportedMeshTopologyBinding[] {
    return [...(this.bodyRecordsByVisibleMeshId.get(mesh.uuid) ?? [])]
  }

  /**
   * 根据 visible GLB 的命中结果解析当前 body 记录。
   *
   * @param hit 第一阶段命中的 visible mesh 命中结果。
   * @returns 对应的 body 记录；无法解析时返回 `null`。
   */
  public resolveBodyRecordFromVisibleHit(hit: THREE.Intersection<THREE.Object3D>): ImportedMeshTopologyBinding | null {
    if (!(hit.object instanceof THREE.Mesh)) {
      return null
    }

    const candidateRecords = this.bodyRecordsByVisibleMeshId.get(hit.object.uuid) ?? []
    if (candidateRecords.length === 0) {
      return null
    }

    const primitiveFaceRanges = resolveVisibleMeshPrimitiveFaceRanges(hit.object)

    if (primitiveFaceRanges.length === 0) {
      return candidateRecords.length === 1 ? candidateRecords[0] ?? null : null
    }

    if (typeof hit.faceIndex !== 'number' || !Number.isInteger(hit.faceIndex)) {
      return null
    }

    const primitiveId = resolvePrimitiveIdFromVisibleHit(hit.object, hit.faceIndex)
    if (!primitiveId) {
      return null
    }

    const bodyBindingId = this.bodyBindingIdByVisibleMeshUuidAndPrimitiveId
      .get(hit.object.uuid)
      ?.get(primitiveId)
    return bodyBindingId
      ? candidateRecords.find((record) => record.bodyBindingId === bodyBindingId) ?? null
      : null
  }

  /**
   * 同步写入主绑定索引和派生 owner 索引，保持两个索引的成员集合一致。
   * @param record 已完成身份与范围校验的绑定。
   */
  private indexBodyRecord(record: ImportedMeshTopologyBinding): void {
    const previousRecord = this.bodyRecordsByBindingId.get(record.bodyBindingId)
    if (previousRecord) this.topologyOwnerIndex.remove(previousRecord)
    this.bodyRecordsByBindingId.set(record.bodyBindingId, record)
    this.topologyOwnerIndex.register(record)
    ImportedModelTopologyRepository.semanticRevision += 1
  }

  /**
   * 收集与指定绑定共享同一 Body occurrence 拓扑所有权的全部显示绑定。
   *
   * @param bodyRecord 目标显示 Mesh 拓扑绑定。
   * @returns 保持 Repository 注册顺序的共享宿主绑定。
   */
  private resolveTopologyOwnerRecords(
    bodyRecord: ImportedMeshTopologyBinding,
  ): readonly ImportedMeshTopologyBinding[] {
    return this.topologyOwnerIndex.resolve(bodyRecord)
  }

  /**
   * 判断目标对象是否包含指定显示 Mesh。
   *
   * @param owner 候选拓扑宿主父节点。
   * @param visibleMesh 当前 Body 的显示 Mesh。
   * @returns visibleMesh 是否等于 owner 或位于 owner 子树中。
   */
  private isVisibleMeshOwnedByObject(owner: THREE.Object3D, visibleMesh: THREE.Mesh): boolean {
    let current: THREE.Object3D | null = visibleMesh
    while (current) {
      if (current === owner) {
        return true
      }
      current = current.parent
    }
    return false
  }

  /**
   * 判断对象是否可以承载拓扑宿主。
   *
   * 除“source 自身是组件变换根”的直接 Mesh 特例外，已注册 visible Mesh 可能被实例化
   * 投影抑制，不能成为共享宿主祖先；候选对象同时必须包含同一 Body occurrence 的全部
   * 材质 Mesh，避免多材质 Body 的宿主脱离业务子树。
   *
   * @param candidate 候选业务父节点。
   * @param ownerRecords 同一 Body occurrence 的全部显示绑定。
   * @returns 可以安全承载拓扑宿主时返回 true。
   */
  private isTopologyHostParentCandidate(
    candidate: THREE.Object3D,
    ownerRecords: readonly ImportedMeshTopologyBinding[],
  ): boolean {
    return !this.bodyRecordsByVisibleMeshId.has(candidate.uuid)
      && ownerRecords.every((record) => this.isVisibleMeshOwnedByObject(candidate, record.visibleMesh))
  }

  /**
   * 解析 source Mesh 自身就是组件变换根的直接拓扑宿主。
   *
   * 单 Mesh occurrence 在普通显示态没有额外组件 Group；若仍把 topology host 提升为兄弟
   * 节点，右键命令把该 occurrence 临时从实例投影回退为普通 Mesh 后，实体旋转将无法通过
   * Three.js 父子变换传递给边线和点线面代理。多材质 Body 或普通组件子 Mesh 不满足本规则，
   * 继续由最近共同业务父节点承载共享 host。
   *
   * @param ownerRecords 同一 Body occurrence 的全部显示绑定。
   * @returns 唯一且自身为组件变换根的 source Mesh；不满足时返回 null。
   */
  private resolveDirectMeshTopologyHostParent(
    ownerRecords: readonly ImportedMeshTopologyBinding[],
  ): THREE.Mesh | null {
    const firstVisibleMesh = ownerRecords[0]?.visibleMesh
    if (
      !firstVisibleMesh
      || ownerRecords.some(record => record.visibleMesh !== firstVisibleMesh)
      || ImportedModelObjectMetadataService.peekOwnMetadata(firstVisibleMesh)
        ?.componentTransformRoot !== true
    ) {
      return null
    }
    return firstVisibleMesh
  }

  /**
   * 解析共享拓扑宿主应该直接挂载的场景父节点。
   *
   * @param ownerRecords 同一 Body occurrence 的全部显示绑定。
   * @param requestedOwner 交互入口显式指定的点击对象。
   * @returns 点击对象或全部材质 Mesh 的最近共同父节点。
   */
  private resolveTopologyHostParent(
    ownerRecords: readonly ImportedMeshTopologyBinding[],
    requestedOwner?: THREE.Object3D,
  ): THREE.Object3D {
    const directMeshParent = this.resolveDirectMeshTopologyHostParent(ownerRecords)
    if (directMeshParent) {
      return directMeshParent
    }

    if (
      requestedOwner
      && this.isTopologyHostParentCandidate(requestedOwner, ownerRecords)
    ) {
      return requestedOwner
    }

    const firstVisibleMesh = ownerRecords[0]?.visibleMesh
    if (!firstVisibleMesh) {
      return this.root
    }

    let candidate: THREE.Object3D | null = firstVisibleMesh.parent
    while (candidate) {
      if (this.isTopologyHostParentCandidate(candidate, ownerRecords)) {
        return candidate
      }
      candidate = candidate.parent
    }
    return this.root
  }

  /**
   * 把拓扑宿主挂到点击对象，并保持其与来源显示 Mesh 一致的世界变换。
   *
   * @param host 当前 Body occurrence 的唯一拓扑宿主。
   * @param sourceVisibleMesh 提供 Body 本地坐标的显示 Mesh。
   * @param targetParent 最终直接父节点。
   */
  private mountPickHost(
    host: FeatureResultMesh,
    sourceVisibleMesh: THREE.Mesh,
    targetParent: THREE.Object3D,
  ): void {
    /**
     * 实例投影激活时可能临时把直接 Mesh 的 host 提升到 source 原父级。在权威挂载入口登记
     * 精确反查锚点，让父链迁移前后的 FeatureFace 都始终回到创建它的 Repository。
     */
    registerImportedModelTopologyRepositoryLookupAnchor(host, this)
    if (this.syncPickHostTransformProjection(sourceVisibleMesh, host, targetParent)) {
      return
    }
    if (host.parent !== targetParent) {
      this.releasePickHostTransformProjection(host)
      if (!host.parent) {
        /** 非实例态的游离宿主 matrix 同样以 source-local 保存，先恢复该坐标语义。 */
        addObject3DToParent(host, sourceVisibleMesh)
      }
      if (host.parent !== targetParent) {
        targetParent.attach(host)
      }
    }
  }

  /**
   * 将已挂载 pick host 绑定到 source Mesh 的活动实例变换投影。
   *
   * @param sourceVisibleMesh 提供权威世界变换的原始显示 Mesh。
   * @param host 当前 Body occurrence 的共享 pick host。
   * @param targetParent 普通显示态下承载该宿主的业务父对象。
   * @returns 存在活动 Unit 并成功建立投影时返回 true。
   */
  private syncPickHostTransformProjection(
    sourceVisibleMesh: THREE.Mesh,
    host: FeatureResultMesh,
    targetParent: THREE.Object3D,
  ): boolean {
    const port = this.topologyHostTransformProjectionPort
    if (!port) {
      return false
    }
    const previousSourceMesh = this.projectedSourceMeshByPickHost.get(host)
    // 位姿变化由 Unit 的正式变换出口推送；同一绑定无需在每次 Hover 再触发一次同步。
    if (previousSourceMesh === sourceVisibleMesh) return true
    if (previousSourceMesh && previousSourceMesh !== sourceVisibleMesh) {
      port.detach(previousSourceMesh, host)
      this.projectedSourceMeshByPickHost.delete(host)
    }
    if (port.attach(sourceVisibleMesh, host, targetParent)) {
      this.projectedSourceMeshByPickHost.set(host, sourceVisibleMesh)
      return true
    }
    return false
  }

  /**
   * 解除指定 pick host 的活动实例变换投影。
   *
   * @param host 即将释放或迁移的 topology pick host。
   */
  private releasePickHostTransformProjection(host: FeatureResultMesh): void {
    const sourceMesh = this.projectedSourceMeshByPickHost.get(host)
    if (!sourceMesh) {
      return
    }
    this.topologyHostTransformProjectionPort?.detach(sourceMesh, host)
    this.projectedSourceMeshByPickHost.delete(host)
  }

  /**
   * 按需创建当前 Body occurrence 的唯一 BRep pick host。
   *
   * @param bodyRecord 目标 body 记录。
   * @param topologyOwner 可选的点击对象；提供时宿主直接挂载到该对象，不挂到材质子 Mesh。
   * @returns 可用于二次局部拓扑仲裁的 pick host。
   */
  public ensurePickHost(
    bodyRecord: ImportedMeshTopologyBinding,
    topologyOwner?: THREE.Object3D,
  ): FeatureResultMesh {
    const expectedObjectId = resolveImportedPickHostObjectId(bodyRecord.topologyAsset)
    const resolvedOwnerRecords = this.resolveTopologyOwnerRecords(bodyRecord)
    const ownerRecords = resolvedOwnerRecords.length > 0 ? resolvedOwnerRecords : [bodyRecord]
    const targetParent = this.resolveTopologyHostParent(ownerRecords, topologyOwner)

    const ownedPickHost = bodyRecord.getPickHost()
    if (
      ownedPickHost
      && ownedPickHost.objectId === expectedObjectId
      && this.ownerRecordsByPickHost.get(ownedPickHost) === ownerRecords
    ) {
      this.mountPickHost(ownedPickHost, ownerRecords[0]!.visibleMesh, targetParent)
      return ownedPickHost
    }

    const currentPickHosts = new Set(
      ownerRecords
        .map((record) => record.getPickHost())
        .filter((host): host is FeatureResultMesh => Boolean(host)),
    )
    const currentPickHost = [...currentPickHosts][0] ?? null
    if (currentPickHost && currentPickHosts.size === 1) {
      const previousOwnerRecords = this.ownerRecordsByPickHost.get(currentPickHost)
      const membersChanged = previousOwnerRecords !== undefined
        && (previousOwnerRecords.length !== ownerRecords.length
          || previousOwnerRecords.some((record, index) => record !== ownerRecords[index]))
      /**
       * BRep hydration 可能在首次 pick 之后才写入 geometryObjectId。
       * 此时需要销毁旧 pick host 并重建，避免 FeatureFace/Line 继续携带 legacy bodyBindingId。
       */
      if (
        currentPickHost.objectId !== expectedObjectId || membersChanged
      ) {
        // 新增材质记录可能还没有 host，必须释放真正持有旧宿主的成员，再重建完整 Provider。
        const previousOwner = ownerRecords.find((record) => record.getPickHost() === currentPickHost)!
        this.disposePickHostForRecord(previousOwner)
      } else {
        ownerRecords.forEach((record) => {
          record.attachPickHost(currentPickHost)
        })
        this.ownerRecordsByPickHost.set(currentPickHost, ownerRecords)
        this.mountPickHost(currentPickHost, ownerRecords[0]?.visibleMesh ?? bodyRecord.visibleMesh, targetParent)
        return currentPickHost
      }
    } else if (currentPickHosts.size > 0) {
      /** 兼容旧运行时已生成的重复宿主：先整组释放，再收口为一个共享宿主。 */
      ownerRecords.forEach((record) => {
        this.disposePickHostForRecord(record)
      })
    }

    const hostOwnerRecord = ownerRecords[0] ?? bodyRecord
    const host = new FeatureResultMesh({
      meshData: {
        ...hostOwnerRecord.topologyAsset.meshData,
        metadata: {
          ...(hostOwnerRecord.topologyAsset.meshData.metadata ?? {}),
          objectId: expectedObjectId,
          geometryObjectId: hostOwnerRecord.topologyAsset.geometryObjectId,
          topologyOwnerKind: 'imported-model-body',
          topologyOwnerId: hostOwnerRecord.bodyBindingId,
          bodyBindingId: hostOwnerRecord.bodyBindingId,
        },
      },
      geometry: createImportedTopologyGeometry(hostOwnerRecord.topologyAsset.meshData),
      material: createImportedTopologyHostMaterial(),
    })
    host.name = `imported-topology-host:${hostOwnerRecord.bodyBindingId}`
    host.userData = {
      ...host.userData,
      skipSelection: true,
      skipSelectionSubtree: true,
      [IMPORTED_TOPOLOGY_HOST_FLAG]: true,
      importedBodyBindingId: hostOwnerRecord.bodyBindingId,
    }
    inheritImportedMetadataToPickHost(host, hostOwnerRecord)
    host.setFeatureFaceGeometryProvider(new ImportedTopologyCompositeFaceGeometryProvider(ownerRecords))
    this.mountPickHost(host, hostOwnerRecord.visibleMesh, targetParent)
    host.syncTopologyVisuals(hostOwnerRecord.topologyAsset.meshData.topologySnapshot ?? null)
    host.setTopologySelectionMode('none')
    ownerRecords.forEach((record) => {
      record.attachPickHost(host)
    })
    this.ownerRecordsByPickHost.set(host, ownerRecords)
    return host
  }

  /**
   * 在保留几何与 Body 身份的前提下暂停一个空闲 pick host。
   *
   * 实例投影必须在宿主仍有有效父级时先解除，随后才能让宿主脱离 Scene；
   * 否则 Unit 重建阶段无法把游离宿主恢复到 source Mesh。
   *
   * @param host 即将进入有界空闲缓存的宿主。
   * @returns 当前宿主确实归此 Repository 管理并已安全脱离时返回 true。
   */
  public suspendPickHost(host: FeatureResultMesh): boolean {
    const ownerRecords = this.ownerRecordsByPickHost.get(host)
    if (!ownerRecords?.some((record) => record.getPickHost() === host)) return false
    const projectedSourceMesh = this.projectedSourceMeshByPickHost.get(host)
    if (projectedSourceMesh) {
      const suspended = this.topologyHostTransformProjectionPort
        ?.suspend(projectedSourceMesh, host) ?? false
      if (!suspended || host.parent) {
        throw new Error(`实例 topology host 无法转入 source-local 缓存态：${projectedSourceMesh.uuid}`)
      }
      this.projectedSourceMeshByPickHost.delete(host)
      return true
    }

    /**
     * 普通 Mesh 没有投影端口；缓存前仍统一把 matrix 规范为 source-local，确保再次挂载时
     * 不会把旧业务父级的局部矩阵重复乘到 source 变换上。
     */
    const sourceVisibleMesh = ownerRecords[0]!.visibleMesh
    if (host.parent && host.parent !== sourceVisibleMesh) {
      sourceVisibleMesh.attach(host)
    }
    host.removeFromParent()
    return true
  }

  /**
   * 释放指定 body 的 pick host。
   *
   * @param bodyBindingId body 绑定 ID。
   */
  public disposePickHost(bodyBindingId: string): void {
    const bodyRecord = this.bodyRecordsByBindingId.get(bodyBindingId)
    if (!bodyRecord) {
      return
    }

    this.disposePickHostForRecord(bodyRecord)
  }

  /**
   * 释放指定 Mesh 绑定所属 Body occurrence 的共享 pick host。
   *
   * 不通过 bodyBindingId 反查，确保同一稳定 ID 的多个 occurrence 都能独立释放。
   *
   * @param bodyRecord 待释放 pick host 的 Mesh 绑定。
   */
  private disposePickHostForRecord(bodyRecord: ImportedMeshTopologyBinding): void {
    const host = bodyRecord.getPickHost()
    if (!host) {
      return
    }

    this.releasePickHostTransformProjection(host)
    this.ownerRecordsByPickHost.delete(host)
    unregisterImportedModelTopologyRepositoryLookupAnchor(host, this)

    /** 同一材质拆分 Body 的全部绑定必须同步解除引用，避免重复释放同一 three.js 资源。 */
    const allRecords = new Set<ImportedMeshTopologyBinding>(this.bodyRecordsByBindingId.values())
    this.bodyRecordsByVisibleMeshId.forEach((records) => {
      records.forEach((record) => allRecords.add(record))
    })
    allRecords.forEach((record) => {
      if (record.getPickHost() === host) {
        record.detachPickHost()
      }
    })
    host.setFeatureFaceGeometryProvider(null)
    host.removeFromParent()
    host.clearTopologyVisuals()
    host.geometry.dispose()
    const material = host.material
    if (Array.isArray(material)) {
      material.forEach((item) => item.dispose())
    } else {
      material.dispose()
    }
  }

  /**
   * 通过 pick host 反查 body 记录。
   *
   * @param host pick host。
   * @returns 命中的 body 记录；不存在时返回 `null`。
   */
  public resolveBodyRecordFromPickHost(host: FeatureResultMesh): ImportedMeshTopologyBinding | null {
    const bodyBindingId = typeof host.userData?.importedBodyBindingId === 'string'
      ? host.userData.importedBodyBindingId
      : ''
    const directRecord = bodyBindingId.length > 0
      ? this.bodyRecordsByBindingId.get(bodyBindingId) ?? null
      : null
    return directRecord
      ?? [...this.bodyRecordsByBindingId.values()].find((record) => record.getPickHost() === host)
      ?? null
  }

  /**
   * 解析 pick host 指定拓扑面实际对应的 source visible Mesh。
   *
   * 多材质 Body 可能由多个 GLB Mesh 共同表达，pick host 只保存一个共享 Body 身份。实例面
   * 投影必须按 face -> triangleRange 正式绑定找到真正提供该面的 Mesh，不能固定使用第一条
   * Body 记录，否则会取得错误的实例 Unit 与局部矩阵。
   *
   * @param host 当前 Body occurrence 的共享 pick host。
   * @param faceIndex 权威拓扑面索引。
   * @returns 显式承载该面的 source Mesh；单 Mesh Body 缺少细分契约时回退自身 Mesh，歧义时返回 null。
   */
  public resolveVisibleMeshForPickHostFace(
    host: FeatureResultMesh,
    faceIndex: number,
  ): THREE.Mesh | null {
    const bodyRecord = this.resolveBodyRecordFromPickHost(host)
    const faceSnapshot = host.getTopologyFaceSnapshotByIndex(faceIndex)
    if (!bodyRecord || !faceSnapshot) {
      return null
    }
    const ownerRecords = this.resolveTopologyOwnerRecords(bodyRecord)
    const matchedRecord = ownerRecords.find(record => (
      // 归属只要求存在真实面片，不要求全部面片能压成单个连续区间。
      record.resolveFaceTriangleRanges(faceSnapshot).length > 0
    ))
    if (matchedRecord) {
      return matchedRecord.visibleMesh
    }
    /** 多 Mesh Body 缺少正式 face range 时无法确定 slot，禁止猜测第一条记录。 */
    return ownerRecords.length === 1 ? bodyRecord.visibleMesh : null
  }

  /**
   * 释放仓库中的全部资源。
   */
  public dispose(): void {
    const allRecords = new Set<ImportedMeshTopologyBinding>(this.bodyRecordsByBindingId.values())
    this.bodyRecordsByVisibleMeshId.forEach((records) => {
      records.forEach((record) => allRecords.add(record))
    })
    allRecords.forEach((bodyRecord) => {
      this.disposePickHostForRecord(bodyRecord)
    })
    if (allRecords.size > 0) ImportedModelTopologyRepository.semanticRevision += 1
    this.runtimeLookupAnchors.forEach((anchor) => {
      unregisterImportedModelTopologyRepositoryLookupAnchor(anchor, this)
    })
    this.runtimeLookupAnchors.clear()
    // 尚未发布的准备仓库或已被替换的旧仓库，不得注销同根上另一个有效仓库。
    if (resolveImportedModelTopologyRepositoryAtRoot(this.root) === this) {
      unregisterImportedModelTopologyRepository(this.root)
    }
    this.bodyRecordsByBindingId.clear()
    this.topologyOwnerIndex.clear()
    this.ownerRecordsByPickHost.clear()
    this.bodyRecordsByVisibleMeshId.clear()
    this.bodyBindingIdByVisibleMeshUuidAndPrimitiveId.clear()
    this.projectedSourceMeshByPickHost.clear()
    this.topologyHostTransformProjectionPort = null
  }
}
